import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, localTimezone } from "@/lib/api";
import { toast } from "sonner";
import { enablePush } from "@/lib/push";

export const DEFAULT_REMINDER = {
  time: "08:00", days: [0, 1, 2, 3, 4, 5, 6], smart: true, message: "",
  channels: { push: true, in_app: true, email: false },
  escalation: { enabled: true, max_snoozes: 3, first_snooze_min: 10, pace: "medium" }, active: true,
};
export const localTz = localTimezone;
const SCROLL_DELAY_MS = 50;

export function useReminders(presetHabitId, onPresetConsumed) {
  const [data, setData] = useState({ reminders: [], channel_setup: {}, push_subscribed: false });
  const [history, setHistory] = useState(null);
  const [editing, setEditing] = useState(null);
  const [enabling, setEnabling] = useState(false);

  const load = useCallback(async () => {
    const [r, h] = await Promise.all([api.get("/reminders"), api.get("/reminders/history")]);
    setData(r.data);
    setHistory(h.data.stats);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!presetHabitId) return;
    const preset = typeof presetHabitId === "string" ? { habit_id: presetHabitId } : presetHabitId;
    setEditing({ preset: preset.habit_id, time: preset.time });
    onPresetConsumed?.();
    setTimeout(() => document.getElementById("reminders")?.scrollIntoView({ behavior: "smooth", block: "start" }), SCROLL_DELAY_MS);
  }, [presetHabitId, onPresetConsumed]);

  const save = async (payload) => {
    try {
      if (editing?.item) await api.put(`/reminders/${editing.item.id}`, payload);
      else await api.post("/reminders", payload);
      toast.success("Reminder saved. We'll nudge you kindly.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => { await api.delete(`/reminders/${id}`); load(); };
  const toggleActive = async (r) => { await api.put(`/reminders/${r.id}`, { ...r, active: !r.active }); load(); };
  const test = async (r) => {
    const res = await api.post(`/reminders/${r.id}/test`);
    toast.success(res.data.push_sent > 0 ? "Test sent — check your notifications." : "Test alarm fired in-app.");
  };

  const enable = async () => {
    setEnabling(true);
    try {
      await enablePush();
      toast.success("Notifications on. Reminders will reach you even when this tab is closed.");
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setEnabling(false);
    }
  };

  const initialFor = () => {
    if (editing?.item) return editing.item;
    if (editing?.preset) return { ...DEFAULT_REMINDER, habit_id: editing.preset, time: editing.time || DEFAULT_REMINDER.time, timezone: localTz() };
    return null;
  };

  return { data, history, editing, setEditing, enabling, save, remove, toggleActive, test, enable, initialFor, reload: load };
}
